import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Search, UserCheck, UserX, Clock, Filter, Crown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Tenant {
  id: string;
  name: string;
  admin_user_id: string;
  status: "active" | "pending" | "suspended" | "trial";
  created_at: string;
  profiles?: {
    full_name: string;
    email: string;
  } | null;
  tenant_subscriptions?: {
    status: string;
    trial_ends_at: string;
    subscription_plans: {
      name: string;
    };
  }[];
}

interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description: string;
}

export function AdminManagementModule() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchTenants();
    fetchSubscriptionPlans();

    // Setup realtime subscription for automatic updates
    const channel = supabase
      .channel('tenants-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tenants'
        },
        () => {
          console.log('Tenants changed, refreshing list...');
          fetchTenants();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles'
        },
        () => {
          console.log('User roles changed, refreshing list...');
          fetchTenants();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchTenants = async () => {
    try {
      const { data, error } = await supabase
        .from("tenants")
        .select(`
          id,
          name,
          admin_user_id,
          status,
          created_at,
          tenant_subscriptions (
            status,
            trial_ends_at,
            subscription_plans (
              name
            )
          )
        `);

      if (error) throw error;

      // Fetch profile data for each tenant separately
      const tenantsWithProfiles = await Promise.all(
        (data || []).map(async (tenant) => {
          let profiles = null;
          if (tenant.admin_user_id) {
            const { data: profileData } = await supabase
              .from("profiles")
              .select("full_name, email")
              .eq("user_id", tenant.admin_user_id)
              .single();
            profiles = profileData;
          }
          return { ...tenant, profiles };
        })
      );

      setTenants(tenantsWithProfiles);
    } catch (error) {
      console.error("Error fetching tenants:", error);
      toast({
        title: "Fehler",
        description: "Admins konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("id, name, slug, description")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setSubscriptionPlans(data || []);
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
    }
  };

  const handleStatusChange = async (tenantId: string, newStatus: "active" | "pending" | "suspended") => {
    try {
      const { error } = await supabase
        .from("tenants")
        .update({ status: newStatus })
        .eq("id", tenantId);

      if (error) throw error;

      await fetchTenants();
      toast({
        title: "Erfolgreich",
        description: "Admin-Status wurde aktualisiert.",
      });
    } catch (error) {
      console.error("Error updating tenant status:", error);
      toast({
        title: "Fehler",
        description: "Status konnte nicht geändert werden.",
        variant: "destructive",
      });
    }
  };

  const handleAssignPlan = async () => {
    if (!selectedTenant || !selectedPlan) return;

    try {
      // Check if tenant already has a subscription
      const { data: existingSubscription, error: checkError } = await supabase
        .from("tenant_subscriptions")
        .select("id")
        .eq("tenant_id", selectedTenant.id)
        .single();

      if (checkError && checkError.code !== "PGRST116") throw checkError;

      if (existingSubscription) {
        // Update existing subscription
        const { error } = await supabase
          .from("tenant_subscriptions")
          .update({
            subscription_plan_id: selectedPlan,
            status: "active",
            current_period_start: new Date().toISOString(),
            current_period_end: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
            updated_at: new Date().toISOString()
          })
          .eq("id", existingSubscription.id);

        if (error) throw error;
      } else {
        // Create new subscription
        const { error } = await supabase
          .from("tenant_subscriptions")
          .insert({
            tenant_id: selectedTenant.id,
            subscription_plan_id: selectedPlan,
            status: "active",
            billing_cycle: "yearly",
            current_period_start: new Date().toISOString(),
            current_period_end: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
          });

        if (error) throw error;
      }

      await fetchTenants();
      setIsAssignDialogOpen(false);
      setSelectedTenant(null);
      setSelectedPlan("");
      
      toast({
        title: "Erfolgreich",
        description: "Abo-Plan wurde zugewiesen.",
      });
    } catch (error) {
      console.error("Error assigning plan:", error);
      toast({
        title: "Fehler",
        description: "Abo-Plan konnte nicht zugewiesen werden.",
        variant: "destructive",
      });
    }
  };

  const openAssignDialog = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setSelectedPlan("");
    setIsAssignDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active: "default" as const,
      pending: "secondary" as const,
      suspended: "destructive" as const,
    };
    return <Badge variant={variants[status as keyof typeof variants] || "secondary"}>{status}</Badge>;
  };

  const getTrialStatus = (tenant: Tenant) => {
    const subscription = tenant.tenant_subscriptions?.[0];
    if (!subscription) return "Kein Abo";
    
    if (subscription.status === "trial" && subscription.trial_ends_at) {
      const daysLeft = Math.ceil((new Date(subscription.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      return daysLeft > 0 ? `${daysLeft} Tage verbleibend` : "Testphase abgelaufen";
    }
    
    if (subscription.status === "active") {
      return subscription.subscription_plans?.name || "Aktives Abo";
    }
    
    return subscription.status;
  };

  const filteredTenants = tenants.filter((tenant) => {
    const matchesSearch = 
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || tenant.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Admin-Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCheck className="h-5 w-5" />
          Admin-Management
        </CardTitle>
        <CardDescription>
          Verwalten Sie alle registrierten Admins und deren Status
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Admin suchen (Name, E-Mail)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="pending">Ausstehend</SelectItem>
              <SelectItem value="suspended">Gesperrt</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-primary">{tenants.length}</div>
            <div className="text-sm text-muted-foreground">Gesamt Admins</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">
              {tenants.filter(t => t.status === "active").length}
            </div>
            <div className="text-sm text-muted-foreground">Aktive Admins</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-600">
              {tenants.filter(t => t.tenant_subscriptions?.[0]?.status === "trial").length}
            </div>
            <div className="text-sm text-muted-foreground">In Testphase</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">
              {tenants.filter(t => t.status === "suspended").length}
            </div>
            <div className="text-sm text-muted-foreground">Gesperrt</div>
          </div>
        </div>

        {/* Admins Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Admin / Unternehmen</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Abo / Testphase</TableHead>
                <TableHead>Registriert</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTenants.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{tenant.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {tenant.profiles?.full_name} • {tenant.profiles?.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(tenant.status)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{getTrialStatus(tenant)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(tenant.created_at).toLocaleDateString("de-DE")}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openAssignDialog(tenant)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <Crown className="h-4 w-4 mr-1" />
                        Abo zuweisen
                      </Button>
                      {tenant.status === "active" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(tenant.id, "suspended")}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <UserX className="h-4 w-4 mr-1" />
                          Sperren
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(tenant.id, "active")}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <UserCheck className="h-4 w-4 mr-1" />
                          Aktivieren
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {filteredTenants.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            Keine Admins gefunden.
          </div>
        )}

        {/* Assign Plan Dialog */}
        <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Abo-Plan zuweisen</DialogTitle>
              <DialogDescription>
                Weisen Sie {selectedTenant?.name} einen kostenlosen Abo-Plan als Promo zu.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Abo-Plan auswählen</label>
                <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                  <SelectTrigger>
                    <SelectValue placeholder="Plan auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {subscriptionPlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        <div>
                          <div className="font-medium">{plan.name}</div>
                          {plan.description && (
                            <div className="text-sm text-muted-foreground">{plan.description}</div>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setIsAssignDialogOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleAssignPlan}
                  disabled={!selectedPlan}
                >
                  Plan zuweisen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}